import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Save, Package, Edit2, X, Check, FolderPlus, GripVertical, Copy, Palette, ChevronUp, ChevronDown } from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Color options for categories
const COLOR_OPTIONS = [
  { value: "cyan", label: "Cyan", class: "bg-cyan-500" },
  { value: "emerald", label: "Emerald", class: "bg-emerald-500" },
  { value: "purple", label: "Purple", class: "bg-purple-500" },
  { value: "amber", label: "Amber", class: "bg-amber-500" },
  { value: "slate", label: "Slate", class: "bg-slate-500" },
  { value: "rose", label: "Rose", class: "bg-rose-500" },
  { value: "blue", label: "Blue", class: "bg-blue-500" },
  { value: "green", label: "Green", class: "bg-green-500" },
  { value: "orange", label: "Orange", class: "bg-orange-500" },
  { value: "pink", label: "Pink", class: "bg-pink-500" },
  { value: "indigo", label: "Indigo", class: "bg-indigo-500" },
  { value: "teal", label: "Teal", class: "bg-teal-500" }
];

// All available fields for items
const AVAILABLE_FIELDS = [
  { key: "name", label: "Name", description: "Item name (always required)" },
  { key: "description", label: "Description", description: "Short description" },
  { key: "price", label: "Price", description: "Price in EUR" },
  { key: "duration", label: "Duration", description: "Duration/length" },
  { key: "max_depth_meters", label: "Max Depth", description: "Maximum depth in meters" },
  { key: "min_age", label: "Minimum Age", description: "Minimum age requirement" },
  { key: "prerequisites", label: "Prerequisites", description: "Requirements" },
  { key: "certification_agency", label: "Certification Agency", description: "e.g., PADI, SSI" },
  { key: "short_description", label: "Short Description", description: "Brief summary" },
  { key: "full_description", label: "Full Description", description: "Detailed description" },
  { key: "num_dives", label: "Number of Dives", description: "Total dives included" },
  { key: "price_single", label: "Price (Single)", description: "Single occupancy price" },
  { key: "price_double", label: "Price (Double)", description: "Double occupancy price" },
  { key: "included", label: "Included", description: "What's included (comma-separated)" },
  { key: "not_included", label: "Not Included", description: "What's not included (comma-separated)" },
  { key: "currency", label: "Currency", description: "Currency code" },
  { key: "notes", label: "Notes", description: "Additional notes" },
  { key: "amount", label: "Amount", description: "Fee amount" }
];

const AdminPackageEditor = () => {
  const [diveCenters, setDiveCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  
  // Category management state
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("cyan");
  const [newCategoryParent, setNewCategoryParent] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryMessage, setCategoryMessage] = useState("");
  
  // Copy structure state
  const [showCopyStructure, setShowCopyStructure] = useState(false);
  const [selectedSourceCenter, setSelectedSourceCenter] = useState("");
  const [copyOverwrite, setCopyOverwrite] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");

  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  // Location Specials state
  const [locations, setLocations] = useState([]);
  const [selectedSpecialsLocation, setSelectedSpecialsLocation] = useState(null);
  const [specialsItems, setSpecialsItems] = useState([]);
  const [specialsSaving, setSpecialsSaving] = useState(false);
  const [showSpecialsEditor, setShowSpecialsEditor] = useState(false);

  // Fetch dive centers and templates on mount
  useEffect(() => {
    fetchDiveCenters();
    fetchTemplates();
    fetchLocations();
  }, []);

  const fetchDiveCenters = async () => {
    try {
      const response = await axios.get(`${API}/map/dive-centers`);
      setDiveCenters(response.data);
    } catch (error) {
      console.error("Error fetching dive centers:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/category-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchLocationSpecials = async (locationId) => {
    try {
      const response = await axios.get(`${API}/location-specials/${locationId}`);
      setSpecialsItems(response.data.items || []);
    } catch {
      setSpecialsItems([]);
    }
  };

  const handleSpecialsLocationSelect = async (locationId) => {
    if (!locationId) {
      setSelectedSpecialsLocation(null);
      setSpecialsItems([]);
      return;
    }
    const loc = locations.find(l => l.id === locationId);
    setSelectedSpecialsLocation(loc);
    await fetchLocationSpecials(locationId);
  };

  const addSpecialsItem = () => {
    setSpecialsItems([...specialsItems, {
      id: crypto.randomUUID(),
      name: "",
      description: "",
      price: "",
      duration: "",
      full_description: "",
      included: [],
      notes: ""
    }]);
  };

  const updateSpecialsItem = (idx, field, value) => {
    const updated = [...specialsItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setSpecialsItems(updated);
  };

  const removeSpecialsItem = (idx) => {
    setSpecialsItems(specialsItems.filter((_, i) => i !== idx));
  };

  const saveLocationSpecials = async () => {
    if (!selectedSpecialsLocation) return;
    setSpecialsSaving(true);
    try {
      const token = localStorage.getItem("token");
      // Clean items: convert price strings to numbers
      const cleaned = specialsItems.map(item => ({
        ...item,
        price: item.price ? parseFloat(item.price) || item.price : undefined,
        included: typeof item.included === 'string' ? item.included.split(',').map(s => s.trim()).filter(Boolean) : (item.included || [])
      }));
      await axios.post(`${API}/admin/location-specials/${selectedSpecialsLocation.id}`, { items: cleaned }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveMessage("Location specials saved!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      setSaveMessage("Error saving specials");
    } finally {
      setSpecialsSaving(false);
    }
  };

  const handleCenterSelect = async (centerId) => {
    if (!centerId) {
      setSelectedCenter(null);
      setPackageData(null);
      setShowCopyStructure(false);
      return;
    }

    setLoading(true);
    try {
      const center = diveCenters.find(c => c.id === centerId);
      setSelectedCenter(center);

      // Fetch existing package data for this center
      const response = await axios.get(`${API}/dive-center-packages/${centerId}`);
      
      // Always allow copy structure (will be shown as a toggle/button)
      setPackageData(response.data);
      
      // Set first parent category as active (no subcategory selected by default)
      if (response.data.categories && response.data.categories.length > 0) {
        const firstParent = response.data.categories.find(c => !c.parent_id);
        if (firstParent) {
          setActiveCategory(firstParent.key);
          setActiveSubcategory(null);
        }
      }
    } catch (error) {
      console.error("Error loading package data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyStructure = async () => {
    if (!selectedSourceCenter || !selectedCenter) return;
    
    if (copyOverwrite && !confirm("This will OVERWRITE all existing package data for this dive center. Are you sure?")) {
      return;
    }
    
    setLoading(true);
    setCopyMessage("");
    const token = localStorage.getItem("token");
    
    try {
      const response = await axios.post(
        `${API}/dive-center-packages/copy-structure/${selectedSourceCenter}/${selectedCenter.id}?overwrite=${copyOverwrite}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCopyMessage(response.data.message ? `✅ ${response.data.message}` : "✅ Structure copied successfully! Reloading...");
      setTimeout(() => {
        handleCenterSelect(selectedCenter.id);
        setShowCopyStructure(false);
        setCopyOverwrite(false);
      }, 1500);
    } catch (error) {
      console.error("Error copying structure:", error);
      setCopyMessage(`❌ ${error.response?.data?.detail || "Failed to copy structure"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!packageData) return;

    setLoading(true);
    setSaveMessage("");
    try {
      // Clean up data before saving - remove ONLY _fieldVisibility (UI-only)
      // KEEP _customFields and _hiddenFields as they need to be persisted
      const cleanData = { ...packageData };
      
      // Helper function to convert comma-separated strings to arrays
      const convertArrayFields = (item) => {
        const converted = { ...item };
        // Convert included and not_included from strings to arrays (if they're strings)
        ['included', 'not_included'].forEach(field => {
          if (converted[field]) {
            if (typeof converted[field] === 'string') {
              // It's a string, split by commas
              converted[field] = converted[field].split(',').map(v => v.trim()).filter(v => v);
            } else if (Array.isArray(converted[field])) {
              // Already an array, ensure all items are strings (not objects)
              converted[field] = converted[field].filter(v => typeof v === 'string' && v.trim());
            }
          }
        });
        return converted;
      };
      
      // Clean all category arrays
      packageData.categories?.forEach(category => {
        if (cleanData[category.key]) {
          cleanData[category.key] = cleanData[category.key].map(item => {
            const { _fieldVisibility, ...cleanItem } = item;
            // Keep _customFields and _hiddenFields - they are essential data
            // Convert string array fields to actual arrays
            return convertArrayFields(cleanItem);
          });
        }
      });
      
      // Also clean legacy arrays
      ['certified_divers', 'try_diving', 'courses_basic', 'courses_specialty', 'specials', 'extra_fees'].forEach(key => {
        if (cleanData[key]) {
          cleanData[key] = cleanData[key].map(item => {
            const { _fieldVisibility, ...cleanItem } = item;
            // Keep _customFields and _hiddenFields - they are essential data
            // Convert string array fields to actual arrays
            return convertArrayFields(cleanItem);
          });
        }
      });
      
      console.log('Saving package data (with custom/hidden fields):', cleanData);
      await axios.post(`${API}/dive-center-packages`, cleanData);
      setSaveMessage("✓ Packages saved successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      console.error("Error saving packages:", error);
      console.error("Error response:", error.response?.data);
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail))
        : error.message;
      setSaveMessage("✗ Error saving packages: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Update functions for Certified Divers
  const updateCertifiedDiver = (index, field, value) => {
    const updated = [...packageData.certified_divers];
    updated[index][field] = value;
    setPackageData({ ...packageData, certified_divers: updated });
  };

  const addCertifiedDiver = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      name: "",
      description: "",
      price: null
    };
    setPackageData({
      ...packageData,
      certified_divers: [...packageData.certified_divers, newItem]
    });
  };

  const removeCertifiedDiver = (index) => {
    const updated = packageData.certified_divers.filter((_, i) => i !== index);
    setPackageData({ ...packageData, certified_divers: updated });
  };

  // Update functions for Try Diving
  const updateTryDiving = (index, field, value) => {
    const updated = [...packageData.try_diving];
    // Keep as string while editing, convert to array on save
    updated[index][field] = value;
    setPackageData({ ...packageData, try_diving: updated });
  };

  const addTryDiving = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      name: "",
      price: null,
      short_description: "",
      full_description: "",
      max_depth_meters: 12,
      min_age: 10,
      duration: "",
      includes: []
    };
    setPackageData({
      ...packageData,
      try_diving: [...packageData.try_diving, newItem]
    });
  };

  const removeTryDiving = (index) => {
    const updated = packageData.try_diving.filter((_, i) => i !== index);
    setPackageData({ ...packageData, try_diving: updated });
  };

  // Update functions for Courses
  const updateCourse = (type, index, field, value) => {
    const key = type === 'basic' ? 'courses_basic' : 'courses_specialty';
    const updated = [...packageData[key]];
    updated[index][field] = value;
    setPackageData({ ...packageData, [key]: updated });
  };

  const addCourse = (type) => {
    const key = type === 'basic' ? 'courses_basic' : 'courses_specialty';
    const newItem = {
      id: `temp-${Date.now()}`,
      name: "",
      price: null,
      duration: "",
      description: "",
      prerequisites: "",
      certification_agency: "PADI"
    };
    setPackageData({
      ...packageData,
      [key]: [...packageData[key], newItem]
    });
  };

  const removeCourse = (type, index) => {
    const key = type === 'basic' ? 'courses_basic' : 'courses_specialty';
    const updated = packageData[key].filter((_, i) => i !== index);
    setPackageData({ ...packageData, [key]: updated });
  };

  // Update functions for Specials
  const updateSpecial = (index, field, value) => {
    const updated = [...packageData.specials];
    // Keep as string while editing, convert to array on save
    updated[index][field] = value;
    setPackageData({ ...packageData, specials: updated });
  };

  const addSpecial = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      name: "",
      duration: "",
      num_dives: 0,
      course: null,
      price_single: null,
      price_double: null,
      included: [],
      not_included: [],
      description: "",
      optional_addon: null
    };
    setPackageData({
      ...packageData,
      specials: [...packageData.specials, newItem]
    });
  };

  const removeSpecial = (index) => {
    const updated = packageData.specials.filter((_, i) => i !== index);
    setPackageData({ ...packageData, specials: updated });
  };

  // Update functions for Extra Fees
  const updateExtraFee = (index, field, value) => {
    const updated = [...packageData.extra_fees];
    updated[index][field] = value;
    setPackageData({ ...packageData, extra_fees: updated });
  };

  const addExtraFee = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      name: "",
      amount: null,
      currency: "EUR",
      notes: ""
    };
    setPackageData({
      ...packageData,
      extra_fees: [...packageData.extra_fees, newItem]
    });
  };

  const removeExtraFee = (index) => {
    const updated = packageData.extra_fees.filter((_, i) => i !== index);
    setPackageData({ ...packageData, extra_fees: updated });
  };

  // Generic functions for dynamic categories
  const updateGenericItem = (categoryKey, index, field, value) => {
    const updated = [...(packageData[categoryKey] || [])];
    
    // For array fields, keep as string while editing, convert to array only when displaying/saving
    // This allows typing commas without them disappearing
    updated[index][field] = value;
    
    setPackageData({ ...packageData, [categoryKey]: updated });
  };

  const addGenericItem = (categoryKey) => {
    // Find the category to get its field_config
    const category = packageData.categories?.find(cat => cat.key === categoryKey);
    const fieldConfig = category?.field_config || {};
    
    const newItem = { 
      id: `temp-${Date.now()}`,
      _customFields: [] // Track custom fields added to this specific item
    };
    
    // Initialize ONLY fields that are visible in the template
    AVAILABLE_FIELDS.forEach(field => {
      const key = field.key;
      const isVisible = fieldConfig[key] === true;
      
      if (!isVisible) return; // Skip invisible fields
      
      // Initialize with appropriate default values
      if (key === 'includes' || key === 'included' || key === 'not_included') {
        newItem[key] = [];
      } else if (key === 'price' || key === 'amount' || key.includes('price_')) {
        newItem[key] = null;
      } else if (key === 'max_depth_meters' || key === 'min_age' || key === 'num_dives') {
        newItem[key] = 0;
      } else if (key === 'currency') {
        newItem[key] = 'EUR';
      } else if (key === 'optional_addon') {
        newItem[key] = null;
      } else {
        newItem[key] = "";
      }
    });
    
    setPackageData({
      ...packageData,
      [categoryKey]: [...(packageData[categoryKey] || []), newItem]
    });
  };

  const addCustomFieldToItem = (categoryKey, index, fieldKey) => {
    const updated = [...(packageData[categoryKey] || [])];
    const item = updated[index];
    
    // Initialize _customFields if not exists
    if (!item._customFields) {
      item._customFields = [];
    }
    
    // Add field to custom fields list
    if (!item._customFields.includes(fieldKey)) {
      item._customFields.push(fieldKey);
    }
    
    // Initialize the field with appropriate default value
    const field = AVAILABLE_FIELDS.find(f => f.key === fieldKey);
    if (field) {
      if (fieldKey === 'includes' || fieldKey === 'included' || fieldKey === 'not_included') {
        item[fieldKey] = [];
      } else if (fieldKey === 'price' || fieldKey === 'amount' || fieldKey.includes('price_')) {
        item[fieldKey] = null;
      } else if (fieldKey === 'max_depth_meters' || fieldKey === 'min_age' || fieldKey === 'num_dives') {
        item[fieldKey] = 0;
      } else if (fieldKey === 'currency') {
        item[fieldKey] = 'EUR';
      } else if (fieldKey === 'optional_addon') {
        item[fieldKey] = null;
      } else {
        item[fieldKey] = "";
      }
    }
    
    setPackageData({ ...packageData, [categoryKey]: updated });
  };

  const removeCustomFieldFromItem = (categoryKey, index, fieldKey) => {
    const updated = [...(packageData[categoryKey] || [])];
    const item = updated[index];
    
    if (item._customFields) {
      item._customFields = item._customFields.filter(f => f !== fieldKey);
    }
    
    // IMPORTANT: Also delete the actual data value
    delete item[fieldKey];
    
    setPackageData({ ...packageData, [categoryKey]: updated });
  };

  const hideTemplateFieldFromItem = (categoryKey, index, fieldKey) => {
    const updated = [...(packageData[categoryKey] || [])];
    const item = updated[index];
    
    // Initialize _hiddenFields if not exists
    if (!item._hiddenFields) {
      item._hiddenFields = [];
    }
    
    // Add field to hidden fields list
    if (!item._hiddenFields.includes(fieldKey)) {
      item._hiddenFields.push(fieldKey);
    }
    
    // IMPORTANT: Also delete the actual data value to prevent it from showing on frontend
    delete item[fieldKey];
    
    setPackageData({ ...packageData, [categoryKey]: updated });
  };

  const showHiddenFieldOnItem = (categoryKey, index, fieldKey) => {
    const updated = [...(packageData[categoryKey] || [])];
    const item = updated[index];
    
    if (item._hiddenFields) {
      item._hiddenFields = item._hiddenFields.filter(f => f !== fieldKey);
    }
    
    setPackageData({ ...packageData, [categoryKey]: updated });
  };

  const removeGenericItem = (categoryKey, index) => {
    const updated = (packageData[categoryKey] || []).filter((_, i) => i !== index);
    setPackageData({ ...packageData, [categoryKey]: updated });
  };

  // Reorder generic items within a category. Direction is -1 (up) or +1 (down).
  const moveGenericItem = (categoryKey, index, direction) => {
    const list = [...(packageData[categoryKey] || [])];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setPackageData({ ...packageData, [categoryKey]: list });
  };

  // Category Management Functions
  const addNewCategory = async () => {
    if (!newCategoryName.trim() || !selectedCenter || !selectedTemplate) return;
    
    const token = localStorage.getItem("token");
    const categoryKey = newCategoryName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    // Get the selected template to extract field_config
    const template = templates.find(t => t.id === selectedTemplate);
    const field_config = {};
    if (template) {
      Object.keys(template.fields).forEach(fieldName => {
        field_config[fieldName] = template.fields[fieldName].visible || false;
      });
    }
    
    const newCategory = {
      id: `cat-${Date.now()}`,
      name: newCategoryName.trim(),
      key: categoryKey,
      order: packageData.categories?.length || 0,
      color: newCategoryColor,
      parent_id: newCategoryParent || null,
      template_id: selectedTemplate,
      field_config: field_config
    };
    
    try {
      await axios.post(
        `${API}/admin/package-categories/${selectedCenter.id}`,
        newCategory,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      const updatedCategories = [...(packageData.categories || []), newCategory];
      setPackageData({
        ...packageData,
        categories: updatedCategories,
        [categoryKey]: []
      });
      
      setNewCategoryName("");
      setNewCategoryColor("cyan");
      setNewCategoryParent(null);
      setSelectedTemplate("");
      setCategoryMessage("✅ Category added successfully!");
      setTimeout(() => setCategoryMessage(""), 3000);
    } catch (error) {
      console.error("Error adding category:", error);
      setCategoryMessage(`❌ ${error.response?.data?.detail || "Failed to add category"}`);
      setTimeout(() => setCategoryMessage(""), 3000);
    }
  };

  const deleteCategory = async (categoryKey) => {
    if (!selectedCenter) return;
    if (!confirm(`Delete category "${categoryKey}"? This will remove all packages in this category.`)) return;
    
    const token = localStorage.getItem("token");
    
    try {
      await axios.delete(
        `${API}/admin/package-categories/${selectedCenter.id}/${categoryKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      const updatedCategories = packageData.categories.filter(cat => cat.key !== categoryKey);
      const updatedData = { ...packageData, categories: updatedCategories };
      delete updatedData[categoryKey];
      
      setPackageData(updatedData);
      
      // Switch to first category if deleted was active
      if (activeCategory === categoryKey && updatedCategories.length > 0) {
        setActiveCategory(updatedCategories[0].key);
      }
      
      setCategoryMessage("✅ Category deleted successfully!");
      setTimeout(() => setCategoryMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting category:", error);
      setCategoryMessage(`❌ ${error.response?.data?.detail || "Failed to delete category"}`);
      setTimeout(() => setCategoryMessage(""), 3000);
    }
  };

  const renameCategory = async (categoryKey, newName) => {
    if (!selectedCenter || !newName.trim()) return;
    
    const token = localStorage.getItem("token");
    
    const updatedCategories = packageData.categories.map(cat =>
      cat.key === categoryKey ? { ...cat, name: newName.trim() } : cat
    );
    
    try {
      await axios.put(
        `${API}/admin/package-categories/${selectedCenter.id}`,
        updatedCategories,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPackageData({ ...packageData, categories: updatedCategories });
      setEditingCategory(null);
      setCategoryMessage("✅ Category renamed successfully!");
      setTimeout(() => setCategoryMessage(""), 3000);
    } catch (error) {
      console.error("Error renaming category:", error);
      setCategoryMessage(`❌ ${error.response?.data?.detail || "Failed to rename category"}`);
      setTimeout(() => setCategoryMessage(""), 3000);
    }
  };

  // Get parent categories (categories without parent_id)
  const getParentCategories = () => {
    // Hide 'specials' — now managed via location-level George Dives Specials editor below
    return packageData?.categories?.filter(cat => !cat.parent_id && cat.key !== 'specials') || [];
  };

  // Get subcategories for a parent
  const getSubcategories = (parentKey) => {
    const parent = packageData?.categories?.find(cat => cat.key === parentKey);
    if (!parent) return [];
    return packageData?.categories?.filter(cat => cat.parent_id === parent.id) || [];
  };

  if (loading && !packageData) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-6 h-6" />
            Package Editor
          </CardTitle>
          <CardDescription>
            Manage all package categories, pricing, and details for each dive center
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dive Center Selector */}
          <div>
            <Label htmlFor="dive-center-select" className="text-base font-semibold mb-2 block">
              Select Dive Center *
            </Label>
            <select
              id="dive-center-select"
              value={selectedCenter?.id || ""}
              onChange={(e) => handleCenterSelect(e.target.value)}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none"
            >
              <option value="">-- Select a Dive Center --</option>
              {diveCenters.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name} - {center.location}, {center.country}
                </option>
              ))}
            </select>
            {selectedCenter && packageData && (
              <p className="text-sm text-gray-600 mt-2">
                {packageData?.is_template_based && packageData?.certified_divers?.length === 0 
                  ? "⚠️ No data found."
                  : "✓ Editing existing package data"}
              </p>
            )}
          </div>

          {/* Copy Structure Option */}
          {selectedCenter && (
            <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Copy className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-lg text-purple-900">Copy Package Structure</h3>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCopyStructure(!showCopyStructure)}
                >
                  {showCopyStructure ? "Hide" : "Show"}
                </Button>
              </div>
              
              {showCopyStructure && (
                <>
                  <p className="text-sm text-gray-700 mb-4">
                    Copy the complete structure (categories and items) from another dive center.
                  </p>
                  <div className="space-y-3">
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Label htmlFor="source-center">Source Dive Center</Label>
                        <select
                          id="source-center"
                          value={selectedSourceCenter}
                          onChange={(e) => setSelectedSourceCenter(e.target.value)}
                          className="w-full p-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                        >
                          <option value="">-- Select Source --</option>
                          {diveCenters
                            .filter(c => c.id !== selectedCenter.id)
                            .map((center) => (
                              <option key={center.id} value={center.id}>
                                {center.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <Button
                        onClick={handleCopyStructure}
                        disabled={!selectedSourceCenter || loading}
                        className="bg-purple-500 hover:bg-purple-600"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                    </div>
                    
                    <div className="flex items-center gap-2 p-2 bg-white rounded border">
                      <Checkbox
                        checked={copyOverwrite}
                        onCheckedChange={(checked) => setCopyOverwrite(checked)}
                        id="overwrite-checkbox"
                      />
                      <label htmlFor="overwrite-checkbox" className="text-sm cursor-pointer">
                        Overwrite existing data (⚠️ This will replace all current packages)
                      </label>
                    </div>
                  </div>
                  
                  {copyMessage && (
                    <div className={`mt-3 p-2 rounded text-sm ${
                      copyMessage.includes('✅') 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {copyMessage}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Category Manager */}
          {packageData && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <FolderPlus className="w-5 h-5 text-cyan-600" />
                  Manage Categories
                </h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCategoryManager(!showCategoryManager)}
                >
                  {showCategoryManager ? "Hide" : "Show"}
                </Button>
              </div>

              {showCategoryManager && (
                <>
                  {/* Add New Category */}
                  <div className="mb-4 p-4 bg-white rounded border">
                    <Label className="text-sm font-semibold mb-3 block">Add New Category</Label>
                    
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="e.g., Advanced Courses"
                          className="flex-1"
                        />
                        <select
                          value={newCategoryColor}
                          onChange={(e) => setNewCategoryColor(e.target.value)}
                          className="p-2 border-2 border-gray-300 rounded-lg"
                        >
                          {COLOR_OPTIONS.map(color => (
                            <option key={color.value} value={color.value}>
                              {color.label}
                            </option>
                          ))}
                        </select>
                        <div className={`w-10 h-10 rounded ${COLOR_OPTIONS.find(c => c.value === newCategoryColor)?.class}`} />
                      </div>
                      
                      <div>
                        <Label className="text-xs text-gray-600 mb-1">Parent Category (optional - for subcategories)</Label>
                        <select
                          value={newCategoryParent || ""}
                          onChange={(e) => setNewCategoryParent(e.target.value || null)}
                          className="w-full p-2 border-2 border-gray-300 rounded-lg"
                        >
                          <option value="">None (Top-level category)</option>
                          {getParentCategories().map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-gray-600 mb-1">Template (defines which fields to show)</Label>
                        <select
                          value={selectedTemplate}
                          onChange={(e) => setSelectedTemplate(e.target.value)}
                          className="w-full p-2 border-2 border-gray-300 rounded-lg"
                        >
                          <option value="">-- Select a template --</option>
                          {templates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.template_name}
                            </option>
                          ))}
                        </select>
                        {selectedTemplate && (
                          <p className="text-xs text-gray-500 mt-1">
                            {templates.find(t => t.id === selectedTemplate)?.description}
                          </p>
                        )}
                      </div>
                      
                      <Button
                        onClick={addNewCategory}
                        disabled={!newCategoryName.trim() || !selectedTemplate}
                        className="w-full bg-cyan-500 hover:bg-cyan-600"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Category
                      </Button>
                    </div>
                  </div>

                  {/* Existing Categories */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Existing Categories</Label>
                    {(() => {
                      // Group categories by parent for proper display
                      const parents = packageData.categories?.filter(c => !c.parent_id && c.key !== 'specials') || [];
                      const children = packageData.categories?.filter(c => c.parent_id) || [];
                      
                      const displayCategories = [];
                      parents.forEach(parent => {
                        displayCategories.push(parent);
                        // Add children right after parent
                        children.filter(c => c.parent_id === parent.id).forEach(child => {
                          displayCategories.push(child);
                        });
                      });
                      // Add orphaned children (shouldn't happen but just in case)
                      children.filter(c => !parents.find(p => p.id === c.parent_id)).forEach(child => {
                        displayCategories.push(child);
                      });
                      
                      return displayCategories.map((category) => (
                        <div
                          key={category.key}
                          className={`flex items-center gap-1.5 sm:gap-2 p-2 bg-white rounded border hover:border-cyan-500 min-w-0 ${category.parent_id ? 'ml-3 sm:ml-6' : ''}`}
                        >
                          <div className={`w-4 h-4 rounded flex-shrink-0 ${COLOR_OPTIONS.find(c => c.value === category.color)?.class || 'bg-gray-400'}`} />
                          <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0 hidden sm:block" />
                          
                          {editingCategory === category.key ? (
                            <>
                              <Input
                                defaultValue={category.name}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    renameCategory(category.key, e.target.value);
                                  }
                                }}
                                className="flex-1"
                                autoFocus
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  const input = document.activeElement;
                                  if (input.value) renameCategory(category.key, input.value);
                                }}
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingCategory(null)}
                              >
                                <X className="w-4 h-4 text-red-600" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <span className="flex-1 min-w-0 font-medium truncate text-sm sm:text-base">
                                {category.parent_id && "↳ "}{category.name}
                              </span>
                              <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded whitespace-nowrap flex-shrink-0">
                                {packageData[category.key]?.length || 0}<span className="hidden sm:inline"> items</span>
                              </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="flex-shrink-0 px-2"
                              onClick={() => setEditingCategory(category.key)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="flex-shrink-0 px-2"
                              onClick={() => deleteCategory(category.key)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                      ));
                    })()}
                  </div>

                  {categoryMessage && (
                    <div className={`mt-3 p-2 rounded text-sm ${
                      categoryMessage.includes('✅') 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {categoryMessage}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Category Tabs */}
          {packageData && !showCopyStructure && (
            <>
              <div className="space-y-2">
                {/* Parent Category Tabs */}
                <div className="flex flex-wrap gap-2 border-b-2 pb-2">
                  {getParentCategories().map((category) => {
                    const colorClass = COLOR_OPTIONS.find(c => c.value === category.color)?.class || 'bg-cyan-500';
                    const hasSubcategories = getSubcategories(category.key).length > 0;
                    
                    return (
                      <button
                        key={category.key}
                        onClick={() => {
                          setActiveCategory(category.key);
                          // Don't auto-select subcategory - show parent content by default
                          setActiveSubcategory(null);
                        }}
                        className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                          activeCategory === category.key
                            ? `${colorClass.replace('bg-', 'bg-')} text-white`
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        {category.name} ({packageData[category.key]?.length || 0})
                      </button>
                    );
                  })}
                </div>
                
                {/* Subcategory Tabs (if active category has subcategories) */}
                {activeCategory && getSubcategories(activeCategory).length > 0 && (
                  <div className="flex flex-wrap gap-2 pl-4">
                    {getSubcategories(activeCategory).map((subcat) => {
                      const colorClass = COLOR_OPTIONS.find(c => c.value === subcat.color)?.class || 'bg-gray-400';
                      return (
                        <button
                          key={subcat.key}
                          onClick={() => setActiveSubcategory(subcat.key)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                            activeSubcategory === subcat.key
                              ? `${colorClass} text-white`
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {subcat.name} ({packageData[subcat.key]?.length || 0})
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Display content based on active category/subcategory */}
              {(() => {
                // If there are subcategories AND one is selected, show subcategory content
                // Otherwise show parent content
                const hasSubcategories = getSubcategories(activeCategory).length > 0;
                const displayKey = (hasSubcategories && activeSubcategory) ? activeSubcategory : activeCategory;
                const displayCategory = packageData.categories?.find(c => c.key === displayKey);
                
                if (!displayKey || !displayCategory) return null;

                // All categories now use template-based rendering
                if (false && displayKey === "certified_divers") {
                  return (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold">Certified Divers - Fun Dive Packages</h3>
                        <Button onClick={addCertifiedDiver} size="sm" className="bg-green-500 hover:bg-green-600">
                          <Plus className="w-4 h-4 mr-1" /> Add Item
                        </Button>
                      </div>
                      
                      {packageData.certified_divers?.map((item, index) => (
                        <Card key={item.id} className="bg-gray-50">
                          <CardContent className="pt-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label>Name *</Label>
                                <Input
                                  value={item.name}
                                  onChange={(e) => updateCertifiedDiver(index, 'name', e.target.value)}
                                  placeholder="e.g., 1 Dive"
                                />
                              </div>
                              <div>
                                <Label>Description</Label>
                                <Input
                                  value={item.description}
                                  onChange={(e) => updateCertifiedDiver(index, 'description', e.target.value)}
                                  placeholder="e.g., Single fun dive"
                                />
                              </div>
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <Label>Price (EUR) *</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.price || ""}
                                    onChange={(e) => updateCertifiedDiver(index, 'price', parseFloat(e.target.value) || null)}
                                    placeholder="28.00"
                                  />
                                </div>
                                <div className="flex items-end gap-1">
                                  <Button
                                    onClick={() => moveGenericItem('certified_divers', index, -1)}
                                    size="sm"
                                    variant="outline"
                                    disabled={index === 0}
                                    data-testid={`move-up-certified-divers-${index}`}
                                    title="Move up"
                                  ><ChevronUp className="w-4 h-4" /></Button>
                                  <Button
                                    onClick={() => moveGenericItem('certified_divers', index, 1)}
                                    size="sm"
                                    variant="outline"
                                    disabled={index === (packageData.certified_divers || []).length - 1}
                                    data-testid={`move-down-certified-divers-${index}`}
                                    title="Move down"
                                  ><ChevronDown className="w-4 h-4" /></Button>
                                  <Button
                                    onClick={() => removeCertifiedDiver(index)}
                                    size="sm"
                                    variant="destructive"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {packageData.certified_divers?.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No items yet. Click "Add Item" to create one.</p>
                      )}
                    </div>
                  );
                }

                if (false && displayKey === "try_diving") {
                  return (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold">Try Diving - Intro Programs</h3>
                        <Button onClick={addTryDiving} size="sm" className="bg-green-500 hover:bg-green-600">
                          <Plus className="w-4 h-4 mr-1" /> Add Item
                        </Button>
                      </div>
                      
                      {packageData.try_diving?.map((item, index) => (
                        <Card key={item.id} className="bg-gray-50">
                          <CardContent className="pt-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label>Name *</Label>
                                <Input
                                  value={item.name}
                                  onChange={(e) => updateTryDiving(index, 'name', e.target.value)}
                                  placeholder="e.g., Discover Scuba Diving"
                                />
                              </div>
                              <div>
                                <Label>Price (EUR) *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.price || ""}
                                  onChange={(e) => updateTryDiving(index, 'price', parseFloat(e.target.value) || null)}
                                  placeholder="52.00"
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label>Duration</Label>
                                <Input
                                  value={item.duration}
                                  onChange={(e) => updateTryDiving(index, 'duration', e.target.value)}
                                  placeholder="Half day (3-4 hours)"
                                />
                              </div>
                              <div>
                                <Label>Max Depth (meters)</Label>
                                <Input
                                  type="number"
                                  value={item.max_depth_meters}
                                  onChange={(e) => updateTryDiving(index, 'max_depth_meters', parseInt(e.target.value))}
                                />
                              </div>
                              <div>
                                <Label>Min Age</Label>
                                <Input
                                  type="number"
                                  value={item.min_age}
                                  onChange={(e) => updateTryDiving(index, 'min_age', parseInt(e.target.value))}
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label>Short Description</Label>
                              <Input
                                value={item.short_description}
                                onChange={(e) => updateTryDiving(index, 'short_description', e.target.value)}
                                placeholder="Brief one-liner"
                              />
                            </div>
                            
                            <div>
                              <Label>Full Description</Label>
                              <Textarea
                                value={item.full_description}
                                onChange={(e) => updateTryDiving(index, 'full_description', e.target.value)}
                                placeholder="Detailed description..."
                                rows={3}
                              />
                            </div>
                            
                            <div>
                              <Label>Includes (one per line)</Label>
                              <Textarea
                                value={item.includes?.join('\n') || ""}
                                onChange={(e) => updateTryDiving(index, 'includes', e.target.value)}
                                placeholder="Basic scuba skills training&#10;Shallow water practice&#10;..."
                                rows={4}
                              />
                            </div>
                            
                            <div className="flex justify-between items-center gap-2 pt-2 border-t">
                              <div className="flex items-center gap-1">
                                <Button
                                  onClick={() => moveGenericItem('try_diving', index, -1)}
                                  size="sm"
                                  variant="outline"
                                  disabled={index === 0}
                                  data-testid={`move-up-try-diving-${index}`}
                                ><ChevronUp className="w-4 h-4" /></Button>
                                <Button
                                  onClick={() => moveGenericItem('try_diving', index, 1)}
                                  size="sm"
                                  variant="outline"
                                  disabled={index === (packageData.try_diving || []).length - 1}
                                  data-testid={`move-down-try-diving-${index}`}
                                ><ChevronDown className="w-4 h-4" /></Button>
                                <span className="text-xs text-gray-500 ml-1">#{index + 1}</span>
                              </div>
                              <Button
                                onClick={() => removeTryDiving(index)}
                                size="sm"
                                variant="destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Remove
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {packageData.try_diving?.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No items yet. Click "Add Item" to create one.</p>
                      )}
                    </div>
                  );
                }

                if (false && displayKey === "courses_basic") {
                  return (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold">Basic Certifications</h3>
                        <Button onClick={() => addCourse('basic')} size="sm" className="bg-green-500 hover:bg-green-600">
                          <Plus className="w-4 h-4 mr-1" /> Add Course
                        </Button>
                      </div>
                      
                      {packageData.courses_basic?.map((item, index) => (
                        <Card key={item.id} className="bg-blue-50">
                          <CardContent className="pt-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label>Name *</Label>
                                <Input
                                  value={item.name}
                                  onChange={(e) => updateCourse('basic', index, 'name', e.target.value)}
                                  placeholder="e.g., Open Water Diver"
                                />
                              </div>
                              <div>
                                <Label>Price (EUR) *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.price || ""}
                                  onChange={(e) => updateCourse('basic', index, 'price', parseFloat(e.target.value) || null)}
                                  placeholder="310.00"
                                />
                              </div>
                              <div>
                                <Label>Duration</Label>
                                <Input
                                  value={item.duration}
                                  onChange={(e) => updateCourse('basic', index, 'duration', e.target.value)}
                                  placeholder="3-4 days"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label>Description</Label>
                              <Textarea
                                value={item.description}
                                onChange={(e) => updateCourse('basic', index, 'description', e.target.value)}
                                placeholder="Course description..."
                                rows={2}
                              />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label>Prerequisites</Label>
                                <Input
                                  value={item.prerequisites || ""}
                                  onChange={(e) => updateCourse('basic', index, 'prerequisites', e.target.value)}
                                  placeholder="e.g., None - beginner friendly"
                                />
                              </div>
                              <div>
                                <Label>Certification Agency</Label>
                                <select
                                  value={item.certification_agency}
                                  onChange={(e) => updateCourse('basic', index, 'certification_agency', e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded-md"
                                >
                                  <option value="PADI">PADI</option>
                                  <option value="SSI">SSI</option>
                                  <option value="NAUI">NAUI</option>
                                  <option value="CMAS">CMAS</option>
                                  <option value="RAID">RAID</option>
                                  <option value="SDI">SDI</option>
                                  <option value="EFR">EFR</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                            </div>
                            
                            <div className="flex justify-between items-center gap-2 pt-2 border-t">
                              <div className="flex items-center gap-1">
                                <Button
                                  onClick={() => moveGenericItem('courses_basic', index, -1)}
                                  size="sm"
                                  variant="outline"
                                  disabled={index === 0}
                                  data-testid={`move-up-courses-basic-${index}`}
                                ><ChevronUp className="w-4 h-4" /></Button>
                                <Button
                                  onClick={() => moveGenericItem('courses_basic', index, 1)}
                                  size="sm"
                                  variant="outline"
                                  disabled={index === (packageData.courses_basic || []).length - 1}
                                  data-testid={`move-down-courses-basic-${index}`}
                                ><ChevronDown className="w-4 h-4" /></Button>
                                <span className="text-xs text-gray-500 ml-1">#{index + 1}</span>
                              </div>
                              <Button
                                onClick={() => removeCourse('basic', index)}
                                size="sm"
                                variant="destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Remove
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {packageData.courses_basic?.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No courses yet. Click "Add Course" to create one.</p>
                      )}
                    </div>
                  );
                }

                if (false && displayKey === "courses_specialty") {
                  return (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold">Specialty Courses</h3>
                        <Button onClick={() => addCourse('specialty')} size="sm" className="bg-green-500 hover:bg-green-600">
                          <Plus className="w-4 h-4 mr-1" /> Add Specialty
                        </Button>
                      </div>
                      
                      {packageData.courses_specialty?.map((item, index) => (
                        <Card key={item.id} className="bg-purple-50">
                          <CardContent className="pt-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label>Name *</Label>
                                <Input
                                  value={item.name}
                                  onChange={(e) => updateCourse('specialty', index, 'name', e.target.value)}
                                  placeholder="e.g., Enriched Air Nitrox"
                                />
                              </div>
                              <div>
                                <Label>Price (EUR) *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.price || ""}
                                  onChange={(e) => updateCourse('specialty', index, 'price', parseFloat(e.target.value) || null)}
                                  placeholder="150.00"
                                />
                              </div>
                              <div>
                                <Label>Duration</Label>
                                <Input
                                  value={item.duration}
                                  onChange={(e) => updateCourse('specialty', index, 'duration', e.target.value)}
                                  placeholder="1 day"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label>Description</Label>
                              <Textarea
                                value={item.description}
                                onChange={(e) => updateCourse('specialty', index, 'description', e.target.value)}
                                placeholder="Course description..."
                                rows={2}
                              />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label>Prerequisites</Label>
                                <Input
                                  value={item.prerequisites || ""}
                                  onChange={(e) => updateCourse('specialty', index, 'prerequisites', e.target.value)}
                                  placeholder="e.g., Open Water Diver"
                                />
                              </div>
                              <div>
                                <Label>Certification Agency</Label>
                                <select
                                  value={item.certification_agency}
                                  onChange={(e) => updateCourse('specialty', index, 'certification_agency', e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded-md"
                                >
                                  <option value="PADI">PADI</option>
                                  <option value="SSI">SSI</option>
                                  <option value="NAUI">NAUI</option>
                                  <option value="CMAS">CMAS</option>
                                  <option value="RAID">RAID</option>
                                  <option value="SDI">SDI</option>
                                  <option value="EFR">EFR</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                            </div>
                            
                            <div className="flex justify-between items-center gap-2 pt-2 border-t">
                              <div className="flex items-center gap-1">
                                <Button
                                  onClick={() => moveGenericItem('courses_specialty', index, -1)}
                                  size="sm"
                                  variant="outline"
                                  disabled={index === 0}
                                  data-testid={`move-up-courses-specialty-${index}`}
                                ><ChevronUp className="w-4 h-4" /></Button>
                                <Button
                                  onClick={() => moveGenericItem('courses_specialty', index, 1)}
                                  size="sm"
                                  variant="outline"
                                  disabled={index === (packageData.courses_specialty || []).length - 1}
                                  data-testid={`move-down-courses-specialty-${index}`}
                                ><ChevronDown className="w-4 h-4" /></Button>
                                <span className="text-xs text-gray-500 ml-1">#{index + 1}</span>
                              </div>
                              <Button
                                onClick={() => removeCourse('specialty', index)}
                                size="sm"
                                variant="destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Remove
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {packageData.courses_specialty?.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No specialties yet. Click "Add Specialty" to create one.</p>
                      )}
                    </div>
                  );
                }

                if (false && displayKey === "specials") {
                  return (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold">Specials - Accommodation Packages</h3>
                        <Button onClick={addSpecial} size="sm" className="bg-green-500 hover:bg-green-600">
                          <Plus className="w-4 h-4 mr-1" /> Add Package
                        </Button>
                      </div>
                      
                      {packageData.specials?.map((item, index) => (
                        <Card key={item.id} className="bg-yellow-50">
                          <CardContent className="pt-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label>Name *</Label>
                                <Input
                                  value={item.name}
                                  onChange={(e) => updateSpecial(index, 'name', e.target.value)}
                                  placeholder="e.g., 5-Day Dive & Stay Package"
                                />
                              </div>
                              <div>
                                <Label>Duration</Label>
                                <Input
                                  value={item.duration}
                                  onChange={(e) => updateSpecial(index, 'duration', e.target.value)}
                                  placeholder="5 nights"
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label>Number of Dives</Label>
                                <Input
                                  type="number"
                                  value={item.num_dives}
                                  onChange={(e) => updateSpecial(index, 'num_dives', parseInt(e.target.value))}
                                />
                              </div>
                              <div>
                                <Label>Price (Single) EUR *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.price_single || ""}
                                  onChange={(e) => updateSpecial(index, 'price_single', parseFloat(e.target.value) || null)}
                                  placeholder="280"
                                />
                              </div>
                              <div>
                                <Label>Price (Double) EUR *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.price_double || ""}
                                  onChange={(e) => updateSpecial(index, 'price_double', parseFloat(e.target.value) || null)}
                                  placeholder="460"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label>Description</Label>
                              <Textarea
                                value={item.description}
                                onChange={(e) => updateSpecial(index, 'description', e.target.value)}
                                placeholder="Package description..."
                                rows={2}
                              />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label>Included (one per line)</Label>
                                <Textarea
                                  value={item.included?.join('\n') || ""}
                                  onChange={(e) => updateSpecial(index, 'included', e.target.value)}
                                  placeholder="6 Dives&#10;Accommodation for 5 nights&#10;..."
                                  rows={5}
                                />
                              </div>
                              <div>
                                <Label>Not Included (one per line)</Label>
                                <Textarea
                                  value={item.not_included?.join('\n') || ""}
                                  onChange={(e) => updateSpecial(index, 'not_included', e.target.value)}
                                  placeholder="Equipment rental&#10;Meals&#10;..."
                                  rows={5}
                                />
                              </div>
                            </div>
                            
                            <div className="flex justify-between items-center gap-2 pt-2 border-t">
                              <div className="flex items-center gap-1">
                                <Button
                                  onClick={() => moveGenericItem('specials', index, -1)}
                                  size="sm"
                                  variant="outline"
                                  disabled={index === 0}
                                  data-testid={`move-up-specials-${index}`}
                                ><ChevronUp className="w-4 h-4" /></Button>
                                <Button
                                  onClick={() => moveGenericItem('specials', index, 1)}
                                  size="sm"
                                  variant="outline"
                                  disabled={index === (packageData.specials || []).length - 1}
                                  data-testid={`move-down-specials-${index}`}
                                ><ChevronDown className="w-4 h-4" /></Button>
                                <span className="text-xs text-gray-500 ml-1">#{index + 1}</span>
                              </div>
                              <Button
                                onClick={() => removeSpecial(index)}
                                size="sm"
                                variant="destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Remove
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {packageData.specials?.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No packages yet. Click "Add Package" to create one.</p>
                      )}
                    </div>
                  );
                }

                if (false && displayKey === "extra_fees") {
                  return (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold">Extra Fees & Add-ons</h3>
                        <Button onClick={addExtraFee} size="sm" className="bg-green-500 hover:bg-green-600">
                          <Plus className="w-4 h-4 mr-1" /> Add Fee
                        </Button>
                      </div>
                      
                      {packageData.extra_fees?.map((item, index) => (
                        <Card key={item.id} className="bg-red-50">
                          <CardContent className="pt-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                <Label>Fee Name *</Label>
                                <Input
                                  value={item.name}
                                  onChange={(e) => updateExtraFee(index, 'name', e.target.value)}
                                  placeholder="e.g., Equipment rental"
                                />
                              </div>
                              <div>
                                <Label>Amount *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.amount || ""}
                                  onChange={(e) => updateExtraFee(index, 'amount', parseFloat(e.target.value) || null)}
                                  placeholder="15.00"
                                />
                              </div>
                              <div>
                                <Label>Currency</Label>
                                <select
                                  value={item.currency}
                                  onChange={(e) => updateExtraFee(index, 'currency', e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded-md"
                                >
                                  <option value="EUR">EUR</option>
                                  <option value="USD">USD</option>
                                  <option value="PHP">PHP</option>
                                  <option value="IDR">IDR</option>
                                  <option value="THB">THB</option>
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <Label>Notes</Label>
                                  <Input
                                    value={item.notes || ""}
                                    onChange={(e) => updateExtraFee(index, 'notes', e.target.value)}
                                    placeholder="Optional notes"
                                  />
                                </div>
                                <div className="flex items-end gap-1">
                                  <Button
                                    onClick={() => moveGenericItem('extra_fees', index, -1)}
                                    size="sm"
                                    variant="outline"
                                    disabled={index === 0}
                                    data-testid={`move-up-extra-fees-${index}`}
                                    title="Move up"
                                  ><ChevronUp className="w-4 h-4" /></Button>
                                  <Button
                                    onClick={() => moveGenericItem('extra_fees', index, 1)}
                                    size="sm"
                                    variant="outline"
                                    disabled={index === (packageData.extra_fees || []).length - 1}
                                    data-testid={`move-down-extra-fees-${index}`}
                                    title="Move down"
                                  ><ChevronDown className="w-4 h-4" /></Button>
                                  <Button
                                    onClick={() => removeExtraFee(index)}
                                    size="sm"
                                    variant="destructive"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {packageData.extra_fees?.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No fees yet. Click "Add Fee" to create one.</p>
                      )}
                    </div>
                  );
                }

                // Generic category form for custom categories
                
                return (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold">{displayCategory.name}</h3>
                      <Button 
                        onClick={() => addGenericItem(displayKey)} 
                        size="sm" 
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Item
                      </Button>
                    </div>
                    
                    {(packageData[displayKey] || []).map((item, index) => {
                      // Get field_config from category's template
                      const fieldConfig = displayCategory?.field_config || {};
                      
                      // Initialize tracking arrays if not exist
                      if (!item._customFields) {
                        item._customFields = [];
                      }
                      if (!item._hiddenFields) {
                        item._hiddenFields = [];
                      }
                      
                      // Get fields to render: template fields + custom fields - hidden fields
                      const templateFields = AVAILABLE_FIELDS.filter(field => fieldConfig[field.key] === true);
                      const customFieldKeys = item._customFields || [];
                      const hiddenFieldKeys = item._hiddenFields || [];
                      const customFields = AVAILABLE_FIELDS.filter(field => customFieldKeys.includes(field.key));
                      
                      // Combine template + custom, then filter out hidden
                      const allFieldsBeforeFiltering = [...templateFields];
                      customFields.forEach(cf => {
                        if (!allFieldsBeforeFiltering.find(f => f.key === cf.key)) {
                          allFieldsBeforeFiltering.push(cf);
                        }
                      });
                      
                      const allFieldsToRender = allFieldsBeforeFiltering.filter(
                        field => !hiddenFieldKeys.includes(field.key)
                      );
                      
                      // Get available fields to add (not in template, not custom, not hidden)
                      const availableToAdd = AVAILABLE_FIELDS.filter(field => 
                        !fieldConfig[field.key] && 
                        !customFieldKeys.includes(field.key) &&
                        !hiddenFieldKeys.includes(field.key)
                      );
                      
                      // Get currently hidden template fields (can be restored)
                      const hiddenTemplateFields = AVAILABLE_FIELDS.filter(field =>
                        fieldConfig[field.key] === true && hiddenFieldKeys.includes(field.key)
                      );
                      
                      return (
                        <Card key={item.id} className="bg-gray-50">
                          <CardContent className="pt-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {/* Render template fields + custom fields */}
                              {allFieldsToRender.map(field => {
                                const isCustomField = customFieldKeys.includes(field.key);
                                const isTemplateField = fieldConfig[field.key] === true;
                                const canHide = field.key !== 'name'; // Can't hide name field
                                
                                return (
                                  <div key={field.key}>
                                    <div className="flex items-center justify-between mb-1">
                                      <Label className="text-sm">
                                        {field.label} {field.key === 'name' && '*'}
                                        {isCustomField && <span className="text-xs text-blue-600 ml-1">(custom)</span>}
                                        {isTemplateField && !isCustomField && <span className="text-xs text-gray-500 ml-1">(template)</span>}
                                      </Label>
                                      <div className="flex gap-1">
                                        {isCustomField && (
                                          <button
                                            onClick={() => removeCustomFieldFromItem(displayKey, index, field.key)}
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="Remove custom field"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        )}
                                        {isTemplateField && canHide && !isCustomField && (
                                          <button
                                            onClick={() => hideTemplateFieldFromItem(displayKey, index, field.key)}
                                            className="text-gray-400 hover:text-gray-600 p-1"
                                            title="Hide this field for this item"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {(() => {
                                      // Render appropriate input based on field type
                                      if (field.key === 'included' || field.key === 'not_included') {
                                        return (
                                          <Textarea
                                            value={Array.isArray(item[field.key]) ? item[field.key].join(', ') : (item[field.key] || "")}
                                            onChange={(e) => updateGenericItem(displayKey, index, field.key, e.target.value)}
                                            placeholder="Separate items with commas (e.g., Equipment rental, Meals, Transportation)"
                                            rows={3}
                                            className="text-sm"
                                          />
                                        );
                                      } else if (field.key === 'full_description' || field.key === 'short_description') {
                                        return (
                                          <Textarea
                                            value={item[field.key] || ""}
                                            onChange={(e) => updateGenericItem(displayKey, index, field.key, e.target.value)}
                                            placeholder={field.description}
                                            rows={2}
                                            className="text-sm"
                                          />
                                        );
                                      } else if (field.key === 'currency') {
                                        return (
                                          <select
                                            value={item[field.key] || "EUR"}
                                            onChange={(e) => updateGenericItem(displayKey, index, field.key, e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                          >
                                            <option value="EUR">EUR</option>
                                            <option value="USD">USD</option>
                                            <option value="PHP">PHP</option>
                                            <option value="IDR">IDR</option>
                                            <option value="THB">THB</option>
                                          </select>
                                        );
                                      } else if (field.key.includes('price') || field.key === 'amount') {
                                        return (
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={item[field.key] || ""}
                                            onChange={(e) => updateGenericItem(displayKey, index, field.key, parseFloat(e.target.value) || null)}
                                            placeholder="0.00"
                                            className="text-sm"
                                          />
                                        );
                                      } else if (field.key === 'max_depth_meters' || field.key === 'min_age' || field.key === 'num_dives') {
                                        return (
                                          <Input
                                            type="number"
                                            value={item[field.key] || ""}
                                            onChange={(e) => updateGenericItem(displayKey, index, field.key, parseInt(e.target.value) || 0)}
                                            placeholder="0"
                                            className="text-sm"
                                          />
                                        );
                                      } else if (field.key === 'certification_agency') {
                                        return (
                                          <select
                                            value={item[field.key] || "PADI"}
                                            onChange={(e) => updateGenericItem(displayKey, index, field.key, e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                          >
                                            <option value="PADI">PADI</option>
                                            <option value="SSI">SSI</option>
                                            <option value="NAUI">NAUI</option>
                                            <option value="CMAS">CMAS</option>
                                            <option value="RAID">RAID</option>
                                            <option value="SDI">SDI</option>
                                            <option value="EFR">EFR</option>
                                            <option value="Other">Other</option>
                                          </select>
                                        );
                                      } else {
                                        return (
                                          <Input
                                            value={item[field.key] || ""}
                                            onChange={(e) => updateGenericItem(displayKey, index, field.key, e.target.value)}
                                            placeholder={field.description}
                                            className="text-sm"
                                          />
                                        );
                                      }
                                    })()}
                                  </div>
                                );
                              })}
                            </div>
                            
                            <div className="flex justify-between items-center pt-2 border-t gap-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* Add Field Dropdown */}
                                {availableToAdd.length > 0 && (
                                  <select
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        addCustomFieldToItem(displayKey, index, e.target.value);
                                        e.target.value = ""; // Reset dropdown
                                      }
                                    }}
                                    className="p-2 border-2 border-green-300 bg-green-50 rounded-md text-sm font-medium text-green-700 hover:bg-green-100 cursor-pointer"
                                  >
                                    <option value="">➕ Add Field</option>
                                    {availableToAdd.map(field => (
                                      <option key={field.key} value={field.key}>
                                        {field.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                
                                {/* Restore Hidden Fields Dropdown */}
                                {hiddenTemplateFields.length > 0 && (
                                  <select
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        showHiddenFieldOnItem(displayKey, index, e.target.value);
                                        e.target.value = ""; // Reset dropdown
                                      }
                                    }}
                                    className="p-2 border-2 border-blue-300 bg-blue-50 rounded-md text-sm font-medium text-blue-700 hover:bg-blue-100 cursor-pointer"
                                  >
                                    <option value="">↩️ Restore Hidden</option>
                                    {hiddenTemplateFields.map(field => (
                                      <option key={field.key} value={field.key}>
                                        {field.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              
                              <div className="flex items-center justify-between gap-2 pt-2 border-t">
                                <div className="flex items-center gap-2">
                                  <Button
                                    onClick={() => moveGenericItem(displayKey, index, -1)}
                                    size="sm"
                                    variant="outline"
                                    disabled={index === 0}
                                    data-testid={`move-up-${displayKey}-${index}`}
                                    title="Move up"
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => moveGenericItem(displayKey, index, 1)}
                                    size="sm"
                                    variant="outline"
                                    disabled={index === (packageData[displayKey] || []).length - 1}
                                    data-testid={`move-down-${displayKey}-${index}`}
                                    title="Move down"
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </Button>
                                  <span className="text-xs text-gray-500 ml-1">#{index + 1}</span>
                                </div>
                                <Button
                                  onClick={() => removeGenericItem(displayKey, index)}
                                  size="sm"
                                  variant="destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" /> Remove Item
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                    
                    {(!packageData[displayKey] || packageData[displayKey].length === 0) && (
                      <p className="text-center text-gray-500 py-8">No items yet. Click "Add Item" to create one.</p>
                    )}
                  </div>
                );
              })()}

              {/* Save Button */}
              {/* Save Button */}
              <div className="flex justify-end items-center gap-4 pt-6 border-t-2">
                {saveMessage && (
                  <span className={`font-semibold ${saveMessage.includes('✓') ? 'text-green-600' : 'text-red-600'}`}>
                    {saveMessage}
                  </span>
                )}
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  size="lg"
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {loading ? "Saving..." : "Save All Packages"}
                </Button>
              </div>
            </>
          )}

          {!packageData && (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Select a dive center above to start editing packages</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── George Dives Specials Editor ── */}
      <Card className="mt-8 border-2 border-amber-200">
        <CardHeader className="cursor-pointer" onClick={() => setShowSpecialsEditor(!showSpecialsEditor)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">George Dives Specials</CardTitle>
                <CardDescription>Location-level special packages (shared across all dive centers at a location)</CardDescription>
              </div>
            </div>
            <span className="text-gray-400 text-sm">{showSpecialsEditor ? 'Collapse' : 'Expand'}</span>
          </div>
        </CardHeader>

        {showSpecialsEditor && (
          <CardContent className="space-y-6">
            {/* Location selector */}
            <div>
              <Label className="text-sm font-medium">Select Location</Label>
              <select
                value={selectedSpecialsLocation?.id || ""}
                onChange={(e) => handleSpecialsLocationSelect(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                data-testid="specials-location-select"
              >
                <option value="">Choose a location...</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name} ({loc.country})</option>
                ))}
              </select>
            </div>

            {selectedSpecialsLocation && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-amber-800">
                    Specials for {selectedSpecialsLocation.name}
                  </h3>
                  <Button size="sm" onClick={addSpecialsItem} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                    <Plus className="w-4 h-4 mr-1" /> Add Special
                  </Button>
                </div>

                {specialsItems.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-6">No specials yet. Click "Add Special" to create one.</p>
                )}

                {specialsItems.map((item, idx) => (
                  <div key={item.id} className="border rounded-lg p-4 space-y-3 bg-amber-50/30">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-amber-800">Special #{idx + 1}</Label>
                      <button onClick={() => removeSpecialsItem(idx)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-gray-500">Name *</Label>
                        <Input value={item.name || ""} onChange={e => updateSpecialsItem(idx, 'name', e.target.value)} placeholder="e.g., Safari Special" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Price (EUR)</Label>
                        <Input value={item.price || ""} onChange={e => updateSpecialsItem(idx, 'price', e.target.value)} placeholder="e.g., 299" type="number" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Duration</Label>
                        <Input value={item.duration || ""} onChange={e => updateSpecialsItem(idx, 'duration', e.target.value)} placeholder="e.g., 3 days" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Short Description</Label>
                        <Input value={item.description || ""} onChange={e => updateSpecialsItem(idx, 'description', e.target.value)} placeholder="Brief summary" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Full Description</Label>
                      <Textarea value={item.full_description || ""} onChange={e => updateSpecialsItem(idx, 'full_description', e.target.value)} rows={2} placeholder="Detailed description..." />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Included (comma separated)</Label>
                      <Input
                        value={Array.isArray(item.included) ? item.included.join(', ') : (item.included || "")}
                        onChange={e => updateSpecialsItem(idx, 'included', e.target.value)}
                        placeholder="e.g., Equipment, Guide, Lunch"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Notes</Label>
                      <Input value={item.notes || ""} onChange={e => updateSpecialsItem(idx, 'notes', e.target.value)} placeholder="Additional notes" />
                    </div>
                  </div>
                ))}

                <Button onClick={saveLocationSpecials} disabled={specialsSaving} className="bg-amber-500 hover:bg-amber-600 text-white w-full" data-testid="save-specials-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {specialsSaving ? "Saving..." : "Save Location Specials"}
                </Button>
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default AdminPackageEditor;
